import i18n from "i18next"
import LanguageDetector from "i18next-browser-languagedetector"
import { initReactI18next } from "react-i18next"

export enum LangCode {
  en = "en",
  es = "es",
  ar = "ar",
  ur = "ur"
}
export const SUPPORTED_LNGS = [LangCode.en, LangCode.es, LangCode.ar, LangCode.ur] as const
const RTL_LANGS = new Set<string>([LangCode.ar, LangCode.ur])

export type AppLanguage = (typeof SUPPORTED_LNGS)[number]

export const NAMESPACES = [
  "account-subtype",
  "bank-account",
  "cash-deposit",
  "cost-center",
  "chart-of-account",
  "customers-partners",
  "dashboard",
  "data-grid",
  "date-range-picker",
  "demand-supply-planning",
  "entity",
  "error",
  "fiscal-year",
  "inventory-management",
  "invoice-policy",
  "invoices",
  "journal-entry",
  "login",
  "production",
  "payment-terms",
  "profit-center",
  "pos",
  "procurement",
  "product-catalog",
  "reports-analytics",
  "sales-orders",
  "fulfillment",
  "sidebar",
  "source-module",
  "system-additional",
  "system"
] as const
export type AppNamespace = (typeof NAMESPACES)[number]

export const isRtlLang = (lng: string | undefined): boolean => RTL_LANGS.has((lng ?? "").split("-")[0])

// Eagerly load every locale JSON via Vite glob so adding a new namespace/language
// only requires creating the file – no edits here.
const localeModules = import.meta.glob<Record<string, unknown>>("./locales/*/*.json", {
  eager: true,
  import: "default"
})

const resources = (() => {
  const acc: Record<string, Record<string, Record<string, unknown>>> = {}
  for (const path in localeModules) {
    const match = path.match(/\.\/locales\/([^/]+)\/(.+)\.json$/)
    if (!match) continue
    const [, lng, ns] = match
    acc[lng] ??= {}
    acc[lng][ns] = localeModules[path]
  }
  return acc
})()

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: LangCode.en,
    supportedLngs: SUPPORTED_LNGS,
    nonExplicitSupportedLngs: true,
    cleanCode: true,
    ns: NAMESPACES,
    defaultNS: "login",
    detection: {
      order: ["localStorage", "navigator", "htmlTag"],
      caches: ["localStorage"],
      lookupLocalStorage: "i18nextLng"
    },
    interpolation: {
      escapeValue: false
    },
    react: {
      useSuspense: false
    }
  })

const applyDocumentLanguageAttrs = (lng: string | undefined): void => {
  if (typeof document === "undefined" || !lng) return
  document.documentElement.dir = isRtlLang(lng) ? "rtl" : "ltr"
  document.documentElement.lang = lng
}

applyDocumentLanguageAttrs(i18n.resolvedLanguage ?? i18n.language)
i18n.on("languageChanged", applyDocumentLanguageAttrs)

export default i18n
